import { z } from 'zod';

import { NetZeroModalSchema } from './NetZero/ModalNetZero/validators';
import {
  EMPTY_FIELD_MESSAGE,
  validateEditorEn,
  validateSpace,
  validateSpaceTextInput,
  validateTextEn,
} from '@Utils/validation';
import { OtherGreenhouseGasModalSchema } from './OtherGreenhouseGas/ModalOtherGreenhouseGas/validators';
import { modalAddCarbonValidationSchema } from './CarbonNeutrality/validator';

enum GH_GOAL_TERM_MESSAGE {
  INTRODUCTION_EN = 'กรุณาระบุคำอธิบายเพิ่มเติมเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุเป็นภาษาอังกฤษ',
  GH_GOALS_OPTION = 'กรุณาเลือกการตั้งเป้าหมายที่บริษัทมีอย่างน้อย 1 ตัวเลือก',
  NET_ZERO_DETAILS = 'กรุณาเพิ่มข้อมูลรายละเอียดการตั้งเป้าหมายการปล่อยก๊าซเรือนกระจกสุทธิเป็นศูนย์ (Net-Zero Greenhouse Gas Emissions)',
  CARBON_DETAILS = 'กรุณาเพิ่มข้อมูลรายละเอียดการตั้งเป้าหมายความเป็นกลางทางคาร์บอน (Carbon Neutrality)​',
  OTHER_DETAILS = 'กรุณาเพิ่มข้อมูลรายละเอียดการตั้งเป้าหมายการลดก๊าซเรือนกระจกอื่น ๆ',
}

const IntroductionSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateEditorEn, GH_GOAL_TERM_MESSAGE.INTRODUCTION_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const RemarkSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateTextEn, GH_GOAL_TERM_MESSAGE.REMARK_EN)
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const GreenhouseGoalSchema = z.object({
  remark: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateTextEn, GH_GOAL_TERM_MESSAGE.REMARK_EN)
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  goals: z
    .object({
      netZero: z.boolean(),
      carbon: z.boolean(),
      other: z.boolean(),
    })
    .refine((goals) => Object.values(goals).some((value) => value), {
      message: GH_GOAL_TERM_MESSAGE.GH_GOALS_OPTION,
    }),
});

const NetZeroSchema = z.object({
  remark: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateTextEn, GH_GOAL_TERM_MESSAGE.REMARK_EN)
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  details: z
    .array(NetZeroModalSchema, {
      message: GH_GOAL_TERM_MESSAGE.NET_ZERO_DETAILS,
    })
    .nonempty({ message: GH_GOAL_TERM_MESSAGE.NET_ZERO_DETAILS }),
});

const CarbonSchema = z.object({
  remark: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateTextEn, {
        message: GH_GOAL_TERM_MESSAGE.REMARK_EN,
      })
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  details: z
    .array(modalAddCarbonValidationSchema, {
      message: GH_GOAL_TERM_MESSAGE.CARBON_DETAILS,
    })
    .nonempty({ message: GH_GOAL_TERM_MESSAGE.CARBON_DETAILS }),
});

const OtherGoalSchema = z.object({
  remark: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateTextEn, GH_GOAL_TERM_MESSAGE.REMARK_EN)
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  details: z
    .array(OtherGreenhouseGasModalSchema, {
      message: GH_GOAL_TERM_MESSAGE.OTHER_DETAILS,
    })
    .nonempty({ message: GH_GOAL_TERM_MESSAGE.OTHER_DETAILS }),
});

export const GreenhouseGoalTermSchema = z.object({
  introduction: IntroductionSchema,
  remark: RemarkSchema,
  greenhouseGoal: GreenhouseGoalSchema,
  netZero: NetZeroSchema,
  other: OtherGoalSchema,
  carbon: CarbonSchema,
});
